import React, { useState } from "react";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Persistent floating escape hatch shown at the top of every page when the
 * admin has toggled "View as New User" shadow mode on. Rendered at the
 * app root (outside the Layout / Onboarding modal tree) with a z-index
 * higher than any Radix Portal so it always stays clickable — even when
 * the onboarding dialog is up over a fresh shadow profile.
 */
export default function ShadowExitBar() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!user || !user.shadow_mode) return null;

  const exit = async () => {
    setBusy(true);
    try {
      await endpoints.adminShadowMode(false);
      await refresh();
      toast.success("Back to your admin view.");
      window.location.assign("/");
    } catch {
      toast.error("Couldn't exit shadow mode.");
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="shadow-exit-bar"
      className="fixed inset-x-0 top-0 flex justify-center px-4 pt-3 pointer-events-none"
      style={{ zIndex: 2147483000 }}
    >
      <div
        className="pointer-events-auto flex items-center gap-3 px-5 py-2.5 rounded-full border hairline shadow-lg"
        style={{ background: "#3a2f24", color: "#f6f1e7" }}
      >
        <span className="text-[10px] uppercase tracking-[0.2em] opacity-80">
          Shadow mode
        </span>
        <span className="text-sm">Viewing as a new user</span>
        <button
          type="button"
          onClick={exit}
          disabled={busy}
          data-testid="btn-shadow-exit"
          className="ml-2 px-3 py-1 rounded-full text-xs tracking-wide transition hover:opacity-90 disabled:opacity-50"
          style={{ background: "#f6f1e7", color: "#3a2f24" }}
        >
          {busy ? "Returning…" : "Return to my account"}
        </button>
      </div>
    </div>
  );
}
